import React from 'react';
import { Download as DownloadIcon, Loader2 } from 'lucide-react';
import { Progress } from 'src/shared/components/ui/progress';
import { Tooltip, TooltipContent, TooltipPortal, TooltipProvider, TooltipTrigger } from 'src/shared/components/ui/tooltip';
import { Button } from 'src/shared/components/ui/button';

export type DownloadProgress = { done: number; total: number };

export type SiteListDownloadButtonProps = {
    onClick: () => void;
    disabled?: boolean;
    isDownloading: boolean;
    progress: DownloadProgress | null;
    showLargePortfolioWarning: boolean;
};

const toPercent = (progress: DownloadProgress) => (progress.done / Math.max(1, progress.total)) * 100;

const SiteListDownloadButton = ({
    onClick,
    disabled = false,
    isDownloading,
    progress,
    showLargePortfolioWarning,
}: SiteListDownloadButtonProps) => {
    const showWarning = showLargePortfolioWarning && !isDownloading;

    return (
        <TooltipProvider delayDuration={200}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <Button
                        variant='outline'
                        onClick={onClick}
                        disabled={disabled || isDownloading}
                        data-testid='site-list-download-button'
                        aria-label='Download data'
                        aria-busy={isDownloading}
                    >
                        {!isDownloading && (
                            <>
                                <span>Download data</span>
                                <DownloadIcon className='w-4 h-4 ml-2' />
                            </>
                        )}
                        {isDownloading && progress && (
                            <div className='flex flex-col gap-1 w-32' data-testid='site-list-download-progress'>
                                <span className='text-xs text-muted-foreground'>Downloading {Math.round(toPercent(progress))}%</span>
                                <Progress value={toPercent(progress)} className='h-1' />
                            </div>
                        )}
                        {isDownloading && !progress && (
                            <>
                                <span>Preparing…</span>
                                <Loader2 className='w-4 h-4 ml-2 animate-spin' data-testid='site-list-download-spinner' />
                            </>
                        )}
                    </Button>
                </TooltipTrigger>
                {showWarning && (
                    <TooltipPortal>
                        <TooltipContent side='bottom' className='max-w-xs' data-testid='site-list-download-warning'>
                            Downloading may take a moment for large portfolios.
                        </TooltipContent>
                    </TooltipPortal>
                )}
            </Tooltip>
        </TooltipProvider>
    );
};

export default SiteListDownloadButton;
